import { STEPS, STEPS_BY_LEG, radioFor } from './steps.js'
import { LEG_IDS, PHASE_STRIP } from '../rails/legs.js'
import { BINDS, bindsForKey, looksLikeACommand } from '../input/DcsKeymap.js'
import { clamp } from '../util/units.js'

/** Seconds to ramp the world to a stop, and to let it go again. */
const FREEZE_S = 0.75
const RELEASE_S = 0.45

/**
 * The instructor.
 *
 * Holds the checklist, decides what you should be doing right now, and stops
 * the pattern when you are not doing it.
 *
 * WHY THE PATTERN STOPS RATHER THAN CARRIES ON
 * Because a trainer that lands you with the gear up has taught you that the
 * gear does not matter. The alternative — press on and mark it failed — is
 * how the real thing works, and it is the right setting once you know the
 * flow, but it is a poor way to *learn* the flow: you arrive at the ramp
 * having failed forty seconds ago with nothing useful to do about it.
 *
 * So when a step is overdue the world eases to a stop over about three
 * quarters of a second, the card pulses, and the moment you press the key it
 * eases back up. Your hands keep working while it is stopped — the throttle
 * still moves, the gear still travels — because freezing those too would mean
 * you could not perform the step you are being held for.
 *
 * WHAT IT COSTS YOU
 * Nothing except the score. Steps done before the pattern needed them are on
 * time; steps that required a hold are late; keys that are not the key are
 * counted. The debrief at the end is the honest version of the pass.
 */
export class ProcedureTrainer {
  constructor(bus = null) {
    this.bus = bus
    this.reset()
  }

  reset(fromLegId = 'INITIAL') {
    /** @type {Record<string, {done:boolean, late:boolean, atTime:number|null}>} */
    this.progress = {}
    const startIndex = LEG_IDS.indexOf(fromLegId)

    for (const step of STEPS) {
      const legIndex = LEG_IDS.indexOf(step.leg)
      // Starting mid-pattern marks the earlier legs' steps as already done,
      // which is the truth: the aeroplane is placed dirty and configured.
      const skipped = legIndex >= 0 && startIndex > legIndex
      this.progress[step.id] = { done: skipped, late: false, atTime: null, skipped }
    }

    this.timeScale = 1
    this.holding = false
    this.holdStepId = null
    this.fumbles = 0
    this.lastFumble = null
    this.flash = ''
    this.flashUntil = 0
    this.legId = fromLegId
    this.complete = false
    this._announced = new Set()
  }

  /* =================================================================== *
   *  INPUT                                                              *
   * =================================================================== */

  /**
   * A recognised DCS bind was pressed. Returns a message worth flashing.
   *
   * The trainer does not *apply* the press — PilotControls does that — it only
   * notices whether it was the one being asked for. Pressing the right key
   * early is fine and always allowed; this is a trainer, not a rhythm game.
   */
  notePress(bindId, snapshot) {
    const current = snapshot?.current
    if (!current) return null
    if (current.key && current.key === bindId) return null
    // A key that belongs to the pattern but is not the one being asked for is
    // not a fumble — you are allowed to work ahead, or put the boards back
    // out. Only a key with no place in the recovery counts.
    return null
  }

  /**
   * A key with no binding at all, or a bind used with the wrong modifier.
   *
   * Produces the single most useful message this program can make: you pressed
   * H for the hook, because that is what every other simulator uses, and DCS
   * wants LCtrl+H.
   *
   * Two things it is careful about. It reports EVERY command on that physical
   * key, not just the first — pick the first and LAlt+G comes back as "G on
   * its own does nothing, that command is G", which is wrong twice over. And
   * it only counts a fumble for keys someone might plausibly be flying with;
   * counting Tab and Escape made the debrief's "wrong keys" figure a lie about
   * a clean pass.
   */
  noteUnbound(code, heldMods) {
    if (!looksLikeACommand(code)) return null

    this.fumbles++
    this.lastFumble = { code, heldMods, at: Date.now() }

    const onThisKey = bindsForKey(code)
    if (!onThisKey.length) return null

    const pressed = heldMods.length
      ? `${heldMods.map(modWord).join(' + ')} + ${describeKey(code)}`
      : describeKey(code)
    const options = onThisKey.map((b) => b.label).join(', ')
    return onThisKey.length === 1
      ? `${pressed} does nothing — that command is ${options}`
      : `${pressed} does nothing — this key is ${options}`
  }

  /* =================================================================== *
   *  UPDATE                                                             *
   * =================================================================== */

  /**
   * @param {number} dt        real seconds, unaffected by the hold
   * @param {import('../rails/RailsFlight.js').RailsFlight} rails
   * @param {import('./PilotControls.js').PilotControls} controls
   */
  update(dt, rails, controls) {
    this.legId = rails.leg.id
    this.complete = rails.isComplete

    // Mark off anything that is now satisfied — but only once its leg is live.
    //
    // The arming rule is not fussiness. Half these steps are satisfied by
    // default before their turn comes: "speedbrake in" is trivially true
    // before you ever put the boards out, "hook up" is true before you put it
    // down, "flaps up" is true for the entire pattern until the downwind.
    // Without arming, a pilot who pressed nothing at all would be credited
    // with four of the twelve items before the break.
    //
    // Arming at the step's OWN leg also gets working ahead right for free: put
    // the gear down early in the break and the step is already satisfied when
    // the downwind arms it, so it is marked done immediately and on time.
    const legIndex = LEG_IDS.indexOf(this.legId)
    for (const step of STEPS) {
      const p = this.progress[step.id]
      if (p.done) continue
      if (legIndex < LEG_IDS.indexOf(step.leg)) continue

      if (step.informational) {
        markDone(p, rails.time, false)
        continue
      }
      if (step.satisfied(controls)) {
        markDone(p, rails.time, this.holding && this.holdStepId === step.id)
        this.bus?.emit('stepDone', { id: step.id, late: p.late })
      }
    }

    // Who is holding us up, if anyone.
    const blocking = this._blockingStep(rails.leg.id)
    this.holding = !!rails.holding && !!blocking
    this.holdStepId = this.holding ? blocking.id : null

    const target = this.holding ? 0 : 1
    const rate = dt / (this.holding ? FREEZE_S : RELEASE_S)
    this.timeScale = clamp(this.timeScale + clamp(target - this.timeScale, -rate, rate), 0, 1)
  }

  /**
   * The rails ask this before every leg change.
   * @returns {true|string} true to proceed, or the reason to wait
   */
  canLeaveLeg(legId) {
    const blocking = this._blockingStep(legId)
    return blocking ? blocking.title : true
  }

  _blockingStep(legId) {
    const steps = STEPS_BY_LEG[legId] ?? []
    return steps.find((s) => s.hold && !this.progress[s.id].done) ?? null
  }

  /** Optional steps are offered, never enforced, and never counted against you. */
  _graded() {
    return STEPS.filter(
      (s) => !this.progress[s.id].skipped && !s.informational && !s.optional,
    )
  }

  /* =================================================================== *
   *  GUIDANCE                                                           *
   * =================================================================== */

  /**
   * Everything the guidance panel needs: what to press now, what is coming,
   * and how far through the pattern you are.
   *
   * "What is coming" deliberately reaches into the *next* leg once the current
   * one is clear, because the useful moment to learn that the gear comes down
   * on the downwind is while you are still in the break.
   */
  snapshot(rails, controls = null) {
    this._controls = controls ?? this._controls
    const legId = rails.leg.id
    const legSteps = STEPS_BY_LEG[legId] ?? []

    const checklist = legSteps.map((s) => ({
      id: s.id,
      title: s.title,
      bindId: s.key ?? null,
      key: s.key ? BINDS[s.key]?.label : null,
      noDcsDefault: !!(s.key && BINDS[s.key]?.noDcsDefault),
      done: this.progress[s.id].done,
      late: this.progress[s.id].late,
      informational: !!s.informational,
      optional: !!s.optional,
    }))

    // An optional step never jumps the queue: it is offered once everything
    // the chart actually requires on this leg is done, so "if desired" cannot
    // hold up something that is not.
    const remaining = legSteps
      .filter((s) => !this.progress[s.id].done)
      .sort((a, b) => (a.optional ? 1 : 0) - (b.optional ? 1 : 0))
    const current = remaining[0] ?? null
    const next = remaining[1] ?? this._firstStepOfNextLeg(legId)

    const phaseIndex = PHASE_STRIP.findIndex((p) => p.id === legId)

    return {
      legId,
      legLabel: rails.leg.subPhase ? rails.leg.subPhase(rails.nav) : rails.leg.label,
      legBrief: rails.leg.brief,
      phaseIndex: phaseIndex >= 0 ? phaseIndex : PHASE_STRIP.length - 1,
      phases: PHASE_STRIP,

      current: current ? this._describe(current) : null,
      next: next ? this._describe(next) : null,
      checklist,

      /** Switch positions, so the panel can show what is armed. */
      systems: this._controls?.snapshot ?? null,

      holding: this.holding,
      holdTitle: this.holdStepId ? (STEPS.find((s) => s.id === this.holdStepId)?.title ?? '') : '',
      timeScale: this.timeScale,

      done: STEPS.filter((s) => this.progress[s.id].done && !this.progress[s.id].skipped).length,
      total: STEPS.filter((s) => !this.progress[s.id].skipped).length,
      /** Taken, not required — shown in the debrief as a note rather than a score. */
      optionalTaken: STEPS.filter((s) => s.optional && this.progress[s.id].done).map((s) => s.title),
      complete: this.complete,
    }
  }

  _firstStepOfNextLeg(legId) {
    const i = LEG_IDS.indexOf(legId)
    for (let j = i + 1; j < LEG_IDS.length; j++) {
      const steps = (STEPS_BY_LEG[LEG_IDS[j]] ?? []).filter((s) => !this.progress[s.id].done)
      if (steps.length) return steps[0]
    }
    return null
  }

  _describe(step) {
    return {
      id: step.id,
      title: step.title,
      /** The bind id, for anything that needs to compare against a keypress. */
      bindId: step.key ?? null,
      /** The label DCS itself uses, for anything that shows it to a person. */
      key: step.key ? BINDS[step.key]?.label : null,
      /** True when DCS ships this command with no keyboard binding at all. */
      noDcsDefault: !!(step.key && BINDS[step.key]?.noDcsDefault),
      keyHint: step.keyHint ?? null,
      optional: !!step.optional,
      cue: step.cue,
      why: step.why,
      radio: radioFor(step, this._controls),
      leg: step.leg,
      informational: !!step.informational,
    }
  }

  /* =================================================================== *
   *  DEBRIEF                                                            *
   * =================================================================== */

  /**
   * The honest version of the pass.
   *
   * Note what is NOT graded: how you flew it. On rails the ball is always
   * centred and the lineup is always good, so an LSO grade would be a
   * flattering lie. What can be graded is whether you knew what to do and
   * when — which is what this trainer is for.
   */
  debrief(rails) {
    const graded = this._graded()
    const late = graded.filter((s) => this.progress[s.id].late)
    const missed = graded.filter((s) => !this.progress[s.id].done)
    const onTime = graded.length - late.length - missed.length

    // Two points a step, one back for a late one, and the fumbles cost a
    // little so that hunting for the key is not free.
    const raw = onTime * 2 + late.length * 1
    const max = graded.length * 2
    const score = clamp((raw - Math.min(this.fumbles, 8) * 0.25) / max, 0, 1)

    return {
      onTime,
      late: late.map((s) => s.title),
      missed: missed.map((s) => s.title),
      /** Chart items marked "if desired" that the pilot chose to take. */
      optionalTaken: STEPS.filter(
        (s) => s.optional && !this.progress[s.id].skipped && this.progress[s.id].done,
      ).map((s) => s.title),
      fumbles: this.fumbles,
      total: graded.length,
      scorePct: Math.round(score * 100),
      grade: gradeFor(score, late.length, missed.length),
      wire: rails.wire,
      grooveTimeS: rails.grooveTimeS,
      touchdownFt: rails.touchdown ? { x: rails.touchdown.x, y: rails.touchdown.y } : null,
      elapsedS: rails.time,
    }
  }
}

/* ===================================================================== *
 *  HELPERS                                                              *
 * ===================================================================== */

function markDone(p, time, late) {
  p.done = true
  p.late = !!late
  p.atTime = time
}

function gradeFor(score, lateCount, missedCount) {
  if (missedCount) return 'INCOMPLETE'
  if (lateCount === 0 && score > 0.97) return 'AHEAD OF THE JET'
  if (score >= 0.85) return 'GOOD PASS'
  if (score >= 0.7) return 'BEHIND THE JET'
  return 'REVIEW THE FLOW'
}

const modWord = (m) => ({ ctrl: 'LCtrl', shift: 'LShift', alt: 'LAlt', meta: 'Cmd' })[m] ?? m

/** Turn a KeyboardEvent.code into something a person would say. */
export function describeKey(code) {
  if (code.startsWith('Key')) return code.slice(3)
  if (code.startsWith('Digit')) return code.slice(5)
  if (code.startsWith('Numpad')) return `Num ${code.slice(6)}`
  const named = {
    Backslash: '\\',
    PageUp: 'Page Up',
    PageDown: 'Page Down',
    Space: 'Space',
    Enter: 'Enter',
    Escape: 'Escape',
  }
  return named[code] ?? code
}
